import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Newspaper, ArrowSquareOut, Clock } from '@phosphor-icons/react';

import { API_URL } from '../config';

const CryptoNewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/crypto-news?limit=30`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceColor = (source) => {
    if (source === 'CoinDesk') return '#007AFF';
    if (source === 'CoinTelegraph') return '#34C759';
    return '#8892B0';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    let clean = html.replace(/<img[^>]*>/gi, '');
    clean = clean.replace(/<[^>]*>/g, '');
    clean = clean.replace(/\s+/g, ' ').trim();
    return clean;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="crypto-news-title">
            Crypto News
          </h1>
          <p className="text-[#8892B0]">Latest headlines from the crypto world</p>
        </div>

        {/* News Feed */}
        {loading ? (
          <div className="text-center py-12 text-[#8892B0]">Loading news...</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 bg-[#131313] border border-white/5">
            <Newspaper size={48} className="mx-auto text-[#5C6B82] mb-4" />
            <p className="text-[#8892B0] mb-2">No news available</p>
            <p className="text-sm text-[#5C6B82]">Check back shortly</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="news-feed">
            {articles.map((article) => (
              <a
                key={article.link}
                href={article.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#131313] border border-white/5 p-5 hover:border-white/20 hover:bg-[#1A1A1A] transition-colors group"
                data-testid={`news-article-${article.link}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="px-2 py-0.5 text-xs uppercase tracking-wider font-bold flex-shrink-0"
                        style={{ backgroundColor: `${getSourceColor(article.source)}20`, color: getSourceColor(article.source) }}
                      >
                        {article.source}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#5C6B82] flex-shrink-0">
                        <Clock size={12} />
                        {formatDate(article.published)}
                      </span>
                    </div>
                    <h3 className="text-white font-bold text-base mb-1 group-hover:text-[#007AFF] transition-colors">
                      {article.title}
                    </h3>
                    {stripHtml(article.summary) && (
                      <p className="text-[#5C6B82] text-sm line-clamp-2">
                        {stripHtml(article.summary)}
                      </p>
                    )}
                  </div>
                  <ArrowSquareOut size={20} className="text-[#5C6B82] group-hover:text-[#007AFF] transition-colors flex-shrink-0 mt-1" />
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoNewsPage;
